import React, {useRef, useState} from 'react';
import {ColumnRequests} from "../../api/requests/ColumnRequests";
import {Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField} from "@mui/material";
import {ProjectJson} from "../../api/json/ProjectJson";
import {AnyColumnJson} from "../../api/json/ColumnJson";
import {allColumnTypes, ColumnType, resolveNameForColumnType} from "../../api/enum/ColumnType";
import AsyncButton from "../button/AsyncButton";
import ColumnPredefinedOptionsEditor from "./ColumnPredefinedOptionsEditor";
import {PredefinedOptionJson} from "../../api/json/PredefinedOptionJson";
import ConfirmDeleteModal from "../modal/ConfirmDeleteModal";
import {useToasts} from "../../hooks/useToasts"
import ApiError from "../../api/ApiError"
import ErrorMessage from "../error/ErrorMessage"
import ColumnNameAndReferenceInput from "./ColumnNameAndReferenceInput"
import useEnterShortcut from "../../hooks/useEnterShortcut"

interface ColumnFormProps {
    project: ProjectJson
    column?: AnyColumnJson
    didCreate: () => void
    didSave: (finished: boolean) => void
    didDelete: () => void
}

function ColumnForm({project, column, didCreate, didSave, didDelete}: ColumnFormProps) {
    const [name, setName] = useState<string>(column?.name ?? "")
    const [type, setType] = useState<ColumnType>(column?.type ?? ColumnType.TEXT)
    const [reference, setReference] = useState<string>(column?.reference ?? "")
    const [helperText, setHelperText] = useState<string>(column?.helperText ?? "")

    const startingOptions = column?.type === ColumnType.PREDEFINED_OPTIONS ? column?.predefinedOptions?.sort((a, b) => a.value.localeCompare(b.value)) ?? [] : []
    const [predefinedOptions, setPredefinedOptions] = useState<PredefinedOptionJson[]>(startingOptions)
    const [numberOfDecimalPlaces, setNumberOfDecimalPlaces] = useState(column?.type === ColumnType.NUMBER ? column?.numberOfDecimalPlaces : 0)

    const [saveInProgress, setSaveInProgress] = useState(false)
    const [error, setError] = useState<ApiError>()

    const [confirmingDelete, setConfirmingDelete] = useState(false)
    const [deleteError, setDeleteError] = useState<ApiError>()

    const toasts = useToasts()

    const ref = useRef<HTMLDivElement>(null)
    useEnterShortcut(ref, () => handleSave())

    const disableSave = name.length === 0
    const handleSave = () => {
        if (disableSave) return
        setSaveInProgress(true)
        column ? handleUpdate(column, undefined, true) : handleCreate()
    }

    const handleCreate = () => {
        ColumnRequests.create(project.id, name, reference, type, helperText, predefinedOptions, numberOfDecimalPlaces)
            .then(didCreate)
            .catch(setError)
            .finally(() => setSaveInProgress(false))
    }

    const handleUpdate = (column: AnyColumnJson, newestPredefinedOptions?: PredefinedOptionJson[], finished: boolean = false) => {
        ColumnRequests.update(project.id, column.id!, name, reference, undefined, helperText, newestPredefinedOptions ?? predefinedOptions, numberOfDecimalPlaces)
            .then(updatedField => {
                didSave(finished)
                if (updatedField.type === ColumnType.PREDEFINED_OPTIONS && updatedField.predefinedOptions) {
                    setPredefinedOptions(updatedField.predefinedOptions)
                }
                toasts.showSaved()
            })
            .catch(setError)
            .finally(() => setSaveInProgress(false))
    }

    const handleDelete = (column: AnyColumnJson) => {
        ColumnRequests.delete(project.id, column.id!)
            .then(() => {
                setConfirmingDelete(false)
                didDelete()
            })
            .catch(error => setDeleteError(error))
    }

    const validateNumberOfDecimalPlaces = (strValue: string) => {
        let numberValue = parseInt(strValue)
        if (isNaN(numberValue)) numberValue = 0
        numberValue = Math.max(Math.min(numberValue, 8), 0)
        return numberValue
    }

    return (
        <Stack spacing={2} ref={ref}>
            {!column &&
                <>
                    <FormControl>
                        <InputLabel id="column-type-select-label">Column Type</InputLabel>
                        <Select
                            labelId="column-type-select-label"
                            value={type}
                            label="Column Type"
                            onChange={(event) => setType(event.target.value as ColumnType)}
                        >
                            {allColumnTypes.map(value => (
                                <MenuItem key={value} value={value}>
                                    {resolveNameForColumnType(value)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </>
            }

            <ColumnNameAndReferenceInput project={project}
                                        column={column}
                                        name={name}
                                        reference={reference}
                                        setName={setName}
                                        setReference={setReference}
            />

            <FormControl>
                <TextField
                    type="text"
                    label="Helper Text"
                    variant="outlined"
                    value={helperText}
                    onChange={(e) => setHelperText(e.target.value.substring(0, 255))}
                />
                <FormHelperText>Instructions that appear with the column.</FormHelperText>
            </FormControl>
            {type === ColumnType.PREDEFINED_OPTIONS &&
                <ColumnPredefinedOptionsEditor
                    project={project}
                    fieldId={column?.id}
                    options={predefinedOptions}
                    updateOptions={(newOptions, saveChanges) => {
                        setPredefinedOptions(newOptions)
                        if (column && saveChanges) handleUpdate(column, newOptions, false)
                    }}
                />
            }
            {type === ColumnType.NUMBER &&
                <TextField
                    type="number"
                    label="Number of Decimal Places"
                    variant="outlined"
                    value={numberOfDecimalPlaces}
                    onChange={(e) => setNumberOfDecimalPlaces(validateNumberOfDecimalPlaces(e.target.value))}/>
            }
            <Stack direction="row" justifyContent={column ? "space-between" : "flex-end"}>
                {column &&
                    <>
                        {confirmingDelete &&
                            <ConfirmDeleteModal
                                heading={`Delete ${column.name} column`}
                                body="All associated column data will be permanently deleted from every row."
                                open={confirmingDelete}
                                setOpen={setConfirmingDelete}
                                confirmedDelete={() => handleDelete(column)}
                                error={deleteError}
                            />
                        }
                        <Button variant="text" color="error" onClick={() => setConfirmingDelete(true)}>Delete</Button>
                    </>

                }
                <AsyncButton
                    waiting={saveInProgress}
                    disabled={disableSave}
                    onClick={handleSave}
                >
                    Save & close
                </AsyncButton>
            </Stack>

            {error && <ErrorMessage error={error}/>}
        </Stack>
    );
}

export default ColumnForm;